import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { ChatAtencion } from '@/components/chat/ChatAtencion';
import { X, MessageCircle, Minimize2, Maximize2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useIsMobile } from '@/hooks/use-mobile';

interface AcceptedRequest {
  id: string;
  profesionalNombre: string;
}

export function FloatingChatPopup() {
  const { user } = useAuth();
  const isMobile = useIsMobile();
  const [activeChat, setActiveChat] = useState<AcceptedRequest | null>(null);
  const [minimized, setMinimized] = useState(false);
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  useEffect(() => {
    if (!user) return;

    const channel = supabase
      .channel(`float-chat-${user.id}`)
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'service_requests',
        filter: `paciente_id=eq.${user.id}`,
      }, async (payload) => {
        const record = payload.new as any;
        if (record.estado === 'aceptada' && record.profesional_id && !dismissed.has(record.id)) {
          // Fetch professional name
          const { data: prof } = await supabase
            .from('profesionales')
            .select('user_id')
            .eq('id', record.profesional_id)
            .single();

          let nombre = 'Profesional';
          if (prof?.user_id) {
            const { data: profile } = await supabase
              .from('profiles')
              .select('nombre, apellido')
              .eq('user_id', prof.user_id)
              .single();
            if (profile) {
              nombre = `${profile.nombre} ${profile.apellido}`.trim();
            }
          }

          setActiveChat({ id: record.id, profesionalNombre: nombre });
          setMinimized(false);
        }
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [user, dismissed]);

  const handleDismiss = () => {
    if (activeChat) {
      setDismissed(prev => new Set(prev).add(activeChat.id));
    }
    setActiveChat(null);
  };

  if (!activeChat) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 100, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 100, scale: 0.9 }}
        transition={{ type: 'spring', damping: 20, stiffness: 300 }}
        className={`fixed z-50 shadow-2xl border border-border rounded-2xl bg-card overflow-hidden flex flex-col ${
          isMobile
            ? minimized ? 'bottom-20 right-4 w-auto' : 'bottom-20 left-3 right-3 max-h-[70vh]'
            : minimized ? 'bottom-6 right-6 w-auto' : 'bottom-6 right-6 w-[380px] max-h-[500px]'
        }`}
        style={isMobile ? { bottom: 'calc(5rem + env(safe-area-inset-bottom, 0px))' } : {}}
      >
        {/* Header */}
        <div className="flex items-center gap-2 px-4 py-3 bg-primary text-primary-foreground">
          <MessageCircle className="w-4 h-4 shrink-0" />
          <div className="flex-1 min-w-0">
            {!minimized && (
              <>
                <p className="text-sm font-semibold truncate">Chat con {activeChat.profesionalNombre}</p>
                <p className="text-[10px] opacity-80">Solicitud aceptada</p>
              </>
            )}
            {minimized && (
              <p className="text-sm font-semibold">Chat activo</p>
            )}
          </div>
          <div className="flex items-center gap-1">
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-primary-foreground hover:bg-primary-foreground/20"
              onClick={() => setMinimized(!minimized)}
            >
              {minimized ? <Maximize2 className="w-3.5 h-3.5" /> : <Minimize2 className="w-3.5 h-3.5" />}
            </Button>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7 text-primary-foreground hover:bg-primary-foreground/20"
              onClick={handleDismiss}
            >
              <X className="w-3.5 h-3.5" />
            </Button>
          </div>
        </div>

        {/* Chat body */}
        {!minimized && (
          <div className="flex-1 overflow-hidden p-3">
            <ChatAtencion requestId={activeChat.id} />
          </div>
        )}
      </motion.div>
    </AnimatePresence>
  );
}
